import { useCallback, useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Grid,
  Divider,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Visibility, VisibilityOff, Google } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { useGoogleLogin } from "@react-oauth/google";
import muzukuruLogo from "../../../assets/muzukurublack.png";
import ctaImage1 from "../../../assets/cta-pattern.png";
import ctaImage2 from "../../../assets/backgroundImage.jpg";
import { RootState } from "../../../store/Index";
import apiService, { BaseURL } from "../../../service/Api";
import { login, setTwoFaUsername, User } from "../../../store/slice/AuthSlice";
import { showErrorToast } from "../../../components/common/ShowToast";
import {
  clearTheme,
  IThemeState,
  loadUserMode,
  setTheme,
} from "../../../store/slice/ThemeSlice";
import { fetchAssignedMemoryBooks } from "../../stream-management/components/memorial-page/comments-compiler/service/memoryBook.api";
import { getHomePage } from "../../../utils/getHomePage";
import CtaSlideshow from "./CTASlideshow";

const Login = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const accessToken = useSelector(
    (state: RootState) => state.auth.access_token,
  );
  const authUser = useSelector((state: RootState) => state.auth.user);

  const ctaImages = [ctaImage1, ctaImage2];

  const muzukuruLogoToUse = reduxTheme.logo || muzukuruLogo;
  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const redirect = redirectParam || getHomePage(authUser?.group_names);

  const handleGoogleAuthCode = useCallback(
    async (code: string) => {
      setIsLoading(true);
      try {
        const response = await apiService.post<{
          access?: string;
          refresh?: string;
          language?: string;
          is_admin?: boolean;
          is_2fa_enabled: boolean;
          has_otp?: boolean;
          user: User;
          message?: string;
        }>("/api/auth/google/", { code }, true);

        if (response.data.is_2fa_enabled) {
          dispatch(setTwoFaUsername(response.data.user.username));
          navigate(`/verify-2fa?redirect=${encodeURIComponent(redirect)}`);
          return;
        }

        const { access, refresh, is_admin } = response.data;
        dispatch(
          login({
            user: {
              username: response.data.user.username,
              first_name: response.data.user.first_name,
              last_name: response.data.user.last_name,
              is_supper_user: response.data.user.is_supper_user,
              permissions: is_admin ? ["admin"] : [],
            },
            access_token: access!,
            refresh_token: refresh!,
            is_2fa_enabled: false,
          }),
        );
        localStorage.setItem("access_token", access!);
        localStorage.setItem("refresh_token", refresh!);
        // Only force 2FA setup for a user who has never generated a secret —
        // has_otp stays true for someone who set it up and later disabled it.
        navigate(response.data.has_otp === false ? "/setup-2fa" : redirect);
      } catch (err: unknown) {
        console.error("Google auth error:", err);
        showErrorToast(
          (err as { response?: { data?: { error?: string } } })?.response?.data
            ?.error || "Google authentication failed. Please try again.",
          "google-error",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, navigate, redirect],
  );

  useEffect(() => {
    if (accessToken) {
      navigate(redirect, { replace: true });
    }
  }, [accessToken, navigate, redirect]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    if (code) {
      handleGoogleAuthCode(code);
    }
  }, [handleGoogleAuthCode, location.search]);

  const googleLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      if (codeResponse.code) {
        handleGoogleAuthCode(codeResponse.code);
      }
    },
    onError: (error) => {
      console.error("Google login error:", error);
      showErrorToast(
        "Google authentication failed. Please try again.",
        "google-error",
      );
    },
    flow: "auth-code",
    redirect_uri: window.location.origin + "/#/accounts/login",
  });

  // ---- Form submit ---- //
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      showErrorToast("Please enter both username and password.", "login-error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.post<{
        access_token?: string;
        refresh_token?: string;
        user: User;
        is_2fa_enabled: boolean;
        has_otp?: boolean;
        message?: string;
      }>("/api/authentication/login/", { username, password }, true);

      if (response.data.is_2fa_enabled) {
        dispatch(setTwoFaUsername(response.data.user.username));
        navigate(`/verify-2fa?redirect=${encodeURIComponent(redirect)}`);
        return;
      }

      // Only force 2FA setup for a user who has never generated a secret —
      // has_otp stays true for someone who set it up and later disabled it.
      const hasOtp = response.data.has_otp !== false;

      dispatch(
        login({
          user: response.data.user,
          access_token: response.data.access_token!,
          refresh_token: response.data.refresh_token!,
          is_2fa_enabled: response.data.is_2fa_enabled,
        }),
      );

      localStorage.setItem("access_token", response.data.access_token!);
      localStorage.setItem("refresh_token", response.data.refresh_token!);

      dispatch(setTwoFaUsername(response.data.user.username));

      dispatch(clearTheme());
      try {
        const themeResponse = await apiService.get<IThemeState>(
          `/api/companies/theme/${encodeURIComponent(username)}`,
        );
        dispatch(setTheme(themeResponse.data));
      } catch (themeErr) {
        console.error("Failed to fetch theme:", themeErr);
      }
      dispatch(loadUserMode(username));

      const destination = hasOtp ? redirect : "/setup-2fa";

      if (!response.data.user?.id) {
        console.error("User ID not found in response");
        navigate(destination);
        return;
      }

      try {
        const assignmentsResponse = await fetchAssignedMemoryBooks(
          response.data.user.id,
        );
        const results = assignmentsResponse.data?.results ?? [];

        const hasActiveAssignments = results.some((assignment) => {
          const physicalComments = assignment.physical_comments || 0;
          const doneComments = assignment.done_comments || 0;
          return physicalComments > 0 && doneComments < physicalComments;
        });

        if (hasActiveAssignments) {
          navigate("/memory-book-dashboard", { state: { showModal: true } });
        } else {
          navigate(destination);
        }
      } catch (err) {
        console.error("Failed to check assignments:", err);
        navigate(destination);
      }
    } catch (err: unknown) {
      const errorInfo = err as {
        message?: string;
        response?: { data?: { message?: string }; status?: number };
      };
      console.error("Login error:", {
        message: errorInfo.message,
        response: errorInfo.response?.data,
        status: errorInfo.response?.status,
      });
      let errorMessage = "An error occurred. Please try again.";
      if (errorInfo.response?.data?.message) {
        errorMessage = errorInfo.response.data.message;
      }
      // Platform gate: a valid account that isn't in an MMS staff group gets a
      // 403 — send them to the styled access-denied page instead of a toast.
      if (errorInfo.response?.status === 403) {
        navigate("/access-denied", { state: { message: errorMessage } });
        return;
      }
      showErrorToast(errorMessage, "login-error");
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5, staggerChildren: 0.1 },
    },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: colors.primary[900],
      height: "52px",
      "& fieldset": { borderColor: "#d4d4d4" },
      "&:hover fieldset": { borderColor: "#a3a3a3" },
      "&.Mui-focused fieldset": { borderColor: "#6b4c3b" },
    },
    "& .MuiInputBase-input": { color: "#1a1a1a", fontSize: "15px" },
  };

  const isStaging =
    BaseURL?.includes("staging.muzukuru.com") || BaseURL?.includes("staging");

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        backgroundColor: colors.primary[900],
        border: isStaging ? "10px solid #FFBB00" : "none",
      }}
    >
      {" "}
      <Grid container sx={{ height: "100%" }}>
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            height: "100%",
            backgroundColor: colors.primary[900],
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            px: { xs: 3, sm: 5, md: 7 },
            py: { xs: 3, md: 4 },
          }}
        >
          {isStaging && (
            <Box
              sx={{
                position: "absolute",
                left: -34,
                top: "50%",
                transform: "translateY(-50%) rotate(-90deg)",
                transformOrigin: "left center",
                backgroundColor: "#FFBB00",
                color: "#fff",
                fontWeight: 700,
                fontSize: "11px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                px: 1.5,
                py: 0.6,
                borderRadius: "0 0 6px 6px",
                zIndex: 1000,
              }}
            >
              STAGING
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: { xs: 3, md: 5 },
            }}
          >
            <Box
              component="img"
              src={muzukuruLogoToUse}
              alt="Muzukuru Funeral Streaming"
              sx={{ height: 48, objectFit: "contain" }}
            />
            <Box
              sx={{
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b4c3b"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M5.5 21a6.5 6.5 0 0 1 13 0" />
              </svg>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {" "}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{ maxWidth: 420, width: "100%" }}
            >
              <Box component="form" onSubmit={handleSubmit}>
                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      mb: 0.5,
                      fontSize: { xs: "26px", md: "30px" },
                    }}
                  >
                    Login
                  </Typography>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: "15px",
                      mb: 4,
                      lineHeight: 1.6,
                    }}
                  >
                    We are happy to see you again!
                  </Typography>
                </motion.div>

                {/* Username */}
                <motion.div variants={itemVariants}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      fontSize: "14px",
                      mb: 1,
                    }}
                  >
                    Username
                  </Typography>
                  <TextField
                    placeholder="john@muzukuru.com"
                    variant="outlined"
                    fullWidth
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isLoading}
                    sx={{ ...fieldSx, mb: 3 }}
                  />
                </motion.div>

                {/* Password */}
                <motion.div variants={itemVariants}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      fontSize: "14px",
                      mb: 1,
                    }}
                  >
                    Password
                  </Typography>
                  <TextField
                    placeholder="••••••••"
                    variant="outlined"
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            disabled={isLoading}
                            sx={{ color: "text.secondary" }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ ...fieldSx, mb: 1.5 }}
                  />
                </motion.div>

                {/* Forgot password */}
                <motion.div variants={itemVariants}>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}
                  >
                    <Link
                      to="/forgot-password"
                      style={{
                        color: "#6b4c3b",
                        textDecoration: "none",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      Forgot Password?
                    </Link>
                  </Box>
                </motion.div>

                {/* Sign In button */}
                <motion.div variants={itemVariants}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    sx={{
                      backgroundColor: "#6b4c3b",
                      textTransform: "none",
                      padding: "14px",
                      borderRadius: "8px",
                      fontSize: "16px",
                      fontWeight: 600,
                      boxShadow: "none",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#55392d",
                        boxShadow: "none",
                      },
                      "&:disabled": {
                        backgroundColor: "rgba(107,76,59,0.4)",
                        color: "#fff",
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </motion.div>

                {/* Divider */}
                <motion.div variants={itemVariants}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 3 }}>
                    <Divider sx={{ flex: 1, borderColor: "rgba(0,0,0,0.1)" }} />
                    <Typography
                      sx={{ color: "text.secondary", fontSize: "14px", fontWeight: 500 }}
                    >
                      or
                    </Typography>
                    <Divider sx={{ flex: 1, borderColor: "rgba(0,0,0,0.1)" }} />
                  </Box>
                </motion.div>

                {/* Google sign-in */}
                <motion.div variants={itemVariants}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => googleLogin()}
                    disabled={isLoading}
                    startIcon={<Google sx={{ color: "#DB4437" }} />}
                    sx={{
                      textTransform: "none",
                      padding: "14px",
                      borderRadius: "8px",
                      borderColor: "#d4d4d4",
                      borderWidth: "1.5px",
                      color: "#1a1a1a",
                      fontSize: "16px",
                      fontWeight: 600,
                      backgroundColor: "#fff",
                      "&:hover": {
                        borderColor: "#a3a3a3",
                        backgroundColor: "#fafafa",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Sign in with Google
                  </Button>
                </motion.div>

                {/* Self-signup removed: staff accounts are provisioned by an admin. */}
              </Box>
            </motion.div>
          </Box>
        </Grid>

        <Grid
          size={{ xs: 0, md: 6 }}
          sx={{
            height: "100%",
            display: { xs: "none", md: "flex" },
            overflow: "hidden",
            backgroundColor: colors.primary[900],
            p: 2,
            boxSizing: "border-box",
          }}
        >
          <CtaSlideshow images={ctaImages} intervalMs={5000} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Login;
